
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';

export interface EmailDomain {
  id: string;
  name: string;
  organization_id: string;
  dns_records?: any;
  status: 'pending' | 'verified' | 'failed';
  verified_at?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export function useEmailDomains() {
  const { currentOrganization } = useOrganization();

  return useQuery({
    queryKey: ['email-domains', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }


      const { data, error } = await supabase
        .from('domains')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching domains:', error);
        throw error;
      }


      return data?.map(domain => ({
        id: domain.id,
        name: domain.name,
        organization_id: domain.organization_id,
        dns_records: domain.dns_records,
        status: domain.status,
        verified_at: domain.verified_at,
        created_by: domain.created_by,
        created_at: domain.created_at,
        updated_at: domain.updated_at,
      })) as EmailDomain[];
    },
    enabled: !!currentOrganization?.id,
  });
}

export function useCreateEmailDomain() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (domainName: string) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected');
      }


      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('domains')
        .insert({
          name: domainName,
          organization_id: currentOrganization.id,
          created_by: user.id,
          status: 'pending',
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating domain:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-domains'] });
      toast({
        title: "Domain added successfully",
        description: "Email domain has been added. Please verify DNS records.",
      });
    },
    onError: (error) => {
      console.error('Domain creation failed:', error);
      toast({
        title: "Error adding domain",
        description: error.message || "Failed to add email domain. Please try again.",
        variant: "destructive",
      });
    },
  });
}
